import { useCallback, useEffect, useReducer, useRef } from 'react';

const ROTATE_DURATION_MS = 1200;
const POP_DURATION_MS = 950;
const POP_PEAK_FRACTION = 0.37;
const POP_PEAK_BUFFER_MS = 150;
const WIDTH_HOLD_MS = Math.round(POP_DURATION_MS * POP_PEAK_FRACTION) + POP_PEAK_BUFFER_MS;
const BOTTOM_REVEAL_DELAY_MS = WIDTH_HOLD_MS + ROTATE_DURATION_MS / 2;
const CLOSE_CLEANUP_MS = Math.max(POP_DURATION_MS, WIDTH_HOLD_MS + ROTATE_DURATION_MS);

interface OpenCloseState {
  isOpening: boolean;
  isHoldingWidth: boolean;
  isPoppingOut: boolean;
  isClosing: boolean;
  isRotatingOnClose: boolean;
  isBottomRevealing: boolean;
}

const CLOSED_STATE: OpenCloseState = {
  isOpening: false,
  isHoldingWidth: false,
  isPoppingOut: false,
  isClosing: false,
  isRotatingOnClose: false,
  isBottomRevealing: false,
};

type Action = 'OPEN' | 'CLOSE_START' | 'CLOSE_WIDTH_RELEASED' | 'CLOSE_BOTTOM_REVEALED' | 'CLOSE_DONE';

function reducer(state: OpenCloseState, action: Action): OpenCloseState {
  switch (action) {
    case 'OPEN':
      return { ...CLOSED_STATE, isOpening: true };
    case 'CLOSE_START':
      return {
        isOpening: false,
        isHoldingWidth: true,
        isPoppingOut: true,
        isClosing: true,
        isRotatingOnClose: false,
        isBottomRevealing: false,
      };
    case 'CLOSE_WIDTH_RELEASED':
      return { ...state, isHoldingWidth: false, isRotatingOnClose: true };
    case 'CLOSE_BOTTOM_REVEALED':
      return { ...state, isBottomRevealing: true };
    case 'CLOSE_DONE':
      return CLOSED_STATE;
    default:
      return state;
  }
}

export function useStampCardOpenCloseAnimation() {
  const [state, dispatch] = useReducer(reducer, CLOSED_STATE);
  const closeTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(
    () => () => {
      closeTimeoutsRef.current.forEach(clearTimeout);
    },
    [],
  );

  const openWidget = useCallback(() => {
    closeTimeoutsRef.current.forEach(clearTimeout);
    closeTimeoutsRef.current = [];
    dispatch('OPEN');
  }, []);

  const closeWidget = useCallback(() => {
    closeTimeoutsRef.current.forEach(clearTimeout);
    closeTimeoutsRef.current = [];
    dispatch('CLOSE_START');
    closeTimeoutsRef.current = [
      setTimeout(() => dispatch('CLOSE_WIDTH_RELEASED'), WIDTH_HOLD_MS),
      setTimeout(() => dispatch('CLOSE_BOTTOM_REVEALED'), BOTTOM_REVEAL_DELAY_MS),
      setTimeout(() => dispatch('CLOSE_DONE'), CLOSE_CLEANUP_MS),
    ];
  }, []);

  const isOpeningRef = useRef(state.isOpening);
  useEffect(() => {
    isOpeningRef.current = state.isOpening;
  }, [state.isOpening]);

  return {
    ...state,
    isOpeningRef,
    openWidget,
    closeWidget,
    popDurationMs: POP_DURATION_MS,
  };
}
